import { STYLES, COLORS, LANGS, NOTE_MAX, QUOTE_MAX } from './anchors';

// ── Records, validation and last-write-wins ──────────────────────────────────
// Every mark is one record per verse:
//   { id, group_id, book, chapter, verse, lang, start, end, quote,
//     style, color, note, updated_at, deleted }
// `id` is made on the device, so a record keeps its identity offline and on
// every device it reaches. Edits never merge field by field: the copy with the
// later updated_at wins whole, and a delete is a record with deleted: true
// (a tombstone) so it can win over an older edit made somewhere else.
// Locally a record may also carry _dirty: true until the server has it.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const BOOK_RE = /^[1-3]?[A-Za-z]{1,5}$/;
const posInt = (n, max) => Number.isInteger(n) && n >= 1 && n <= max;

// A clean copy of `rec` with only the stored fields, or null when it would not
// pass the server's checks (src/sql/annotations.sql applies the same rules).
// Used both for what this device sends and for what it receives.
export function sanitizeRecord(rec) {
  if (!rec || typeof rec !== 'object') return null;
  const start = Number(rec.start), end = Number(rec.end);
  const chapter = Number(rec.chapter), verse = Number(rec.verse);
  const time = Date.parse(rec.updated_at);
  if (!UUID_RE.test(rec.id || '') || !UUID_RE.test(rec.group_id || '')) return null;
  if (!BOOK_RE.test(rec.book || '') || !posInt(chapter, 150) || !posInt(verse, 200)) return null;
  if (!LANGS.includes(rec.lang) || !STYLES.includes(rec.style) || !COLORS.includes(rec.color)) return null;
  if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end <= start || end > 20000) return null;
  if (Number.isNaN(time)) return null;
  const deleted = !!rec.deleted;
  const note = !deleted && typeof rec.note === 'string' && rec.note.trim()
    ? rec.note.slice(0, NOTE_MAX) : null;
  return {
    id: rec.id.toLowerCase(), group_id: rec.group_id.toLowerCase(),
    book: rec.book, chapter, verse, lang: rec.lang, start, end,
    quote: String(rec.quote || '').slice(0, QUOTE_MAX),
    style: rec.style, color: rec.color, note,
    updated_at: new Date(time).toISOString(), deleted,
  };
}

// Which of two copies of the same record wins. Later updated_at wins; on an
// exact tie a delete wins (so a mark removed on one device does not come back),
// and otherwise the second argument — the server's copy — wins, so every
// device settles on the same version.
export function newer(local, remote) {
  const a = Date.parse(local.updated_at) || 0, b = Date.parse(remote.updated_at) || 0;
  if (a !== b) return a > b ? local : remote;
  if (!!local.deleted !== !!remote.deleted) return local.deleted ? local : remote;
  return remote;
}

// Folds records from the server into the local list. Returns the new list and
// whether anything changed. A local copy that is newer stays and is flagged to
// be sent again, in case the server never got it.
export function mergeRecords(local, remote) {
  const byId = new Map(local.map(r => [r.id, r]));
  let changed = false;
  for (const raw of remote) {
    const r = sanitizeRecord(raw);
    if (!r) continue;
    const mine = byId.get(r.id);
    if (!mine) { byId.set(r.id, r); changed = true; continue; }
    if (newer(mine, r) === r) {
      if (!sameRecord(mine, r) || mine._dirty) { byId.set(r.id, r); changed = true; }
    } else if (!mine._dirty) {
      byId.set(r.id, { ...mine, _dirty: true });
      changed = true;
    }
  }
  return { records: changed ? [...byId.values()] : local, changed };
}

const FIELDS = ['group_id', 'book', 'chapter', 'verse', 'lang', 'start', 'end', 'quote',
                'style', 'color', 'note', 'updated_at', 'deleted'];
function sameRecord(a, b) {
  return FIELDS.every(f => (a[f] ?? null) === (b[f] ?? null));
}

// Adds records made while signed out to an account's list. Ids are unique, so
// nothing collides; all of them are flagged to go up on the next sync.
export function adoptRecords(accountRecords, guestRecords) {
  const ids = new Set(accountRecords.map(r => r.id));
  const adopted = guestRecords.filter(r => !ids.has(r.id) && !r.deleted).map(r => ({ ...r, _dirty: true }));
  return adopted.length ? [...accountRecords, ...adopted] : accountRecords;
}
